import { Button, makeStyles, Text, tokens } from '@fluentui/react-components';
import { ArrowLeftRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChatRoute } from '../Routes';
import AppLogoWithTitle from '../Components/Common/AppLogoWithTitle';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: `${tokens.spacingVerticalM} ${tokens.spacingVerticalL}`,
        paddingTop: tokens.spacingVerticalXS,
        backgroundColor: tokens.colorNeutralBackground1,
        textAlign: 'center',
        '@media (max-width: 768px)': {
            padding: `${tokens.spacingVerticalL} ${tokens.spacingVerticalL}`,
            paddingTop: tokens.spacingVerticalXS,
        },
    },
    logoContainer: {
        margin: 0,
        marginTop: '-50px',
        marginBottom: tokens.spacingVerticalXXL, // Increased spacing below SyGPT icon
    },
    errorIcon: {
        fontSize: '64px',
        color: tokens.colorPaletteRedForeground1,
        marginBottom: `calc(${tokens.spacingVerticalXXL} * 1.5)`, 
    },
    title: {
        fontSize: tokens.fontSizeHero700,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalXXL,
    },
    errorCode: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground3,
        marginBottom: tokens.spacingVerticalL,
        fontFamily: tokens.fontFamilyMonospace,
        backgroundColor: tokens.colorNeutralBackground3,
        padding: tokens.spacingHorizontalS,
        borderRadius: tokens.borderRadiusSmall,
        display: 'inline-block',
    },
    message: {
        fontSize: tokens.fontSizeBase600,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        marginBottom: `calc(${tokens.spacingVerticalXXL} * 2)`, 
        maxWidth: '600px',
        lineHeight: '1.5',
    },
    actions: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
});

// This error page should show to users who:
// - Have successfully completed the initial login but face subsequent authentication issues
// - Experience failures during the authentication flow itself
// - Are authenticated but lack proper permissions/consent

export const PageError: FC = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const errorCode = searchParams.get('errorCode') || 'unknown_error';
    const errorMessage = searchParams.get('errorMessage') || t('error.generic.unknownError');

    const handleGoHome = () => {
        navigate(ChatRoute);
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    return (
        <div className={classes.container}>
            <div className={classes.logoContainer}>
                <AppLogoWithTitle />
            </div>
            
            <ErrorCircleRegular className={classes.errorIcon} />
            
            <Text className={classes.title}>
                {t('error.page.title')}
            </Text>
            
            {/* <div className={classes.errorCode}>
                {t('error.page.errorCode')}: {errorCode}
            </div> */}
            
            <Text className={classes.message}>
                {errorMessage}
            </Text>
            
            <div className={classes.actions}>
                <Button
                    appearance="primary"
                    icon={<ArrowLeftRegular />}
                    onClick={handleGoHome}
                >
                    {t('error.page.goToHome')}
                </Button>
                
                <Button
                    appearance="secondary"
                    onClick={handleGoBack}
                >
                    {t('error.page.goBack')}
                </Button>
            </div>
            
            <Text style={{ marginTop: tokens.spacingVerticalL, color: tokens.colorNeutralForeground3 }}>
                Go talk to your admin if you do not have access
            </Text>
        </div>
    );
};
